import { Box } from '@mui/material'
import {
  Status, UpdateIncentiveInput, useCompaniesNameOnlyQuery,
  useDeleteFileMutation, useIncentiveQuery, useUpdateFileMutation, useUpdateIncentiveMutation
} from '@typings/graphql'
import { isRole } from '@utils/permissions'
import React from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router'
import { DeleteIconButton } from '@shared/components/ui/DeleteIconButton'
import { CreateEditLayout } from '@shared/layouts/CreateEditLayout'
import { StatusSelectInput } from '@shared/components/inputs/StatusSelectInput'
import { LoadingButton } from '@mui/lab'
import { FormProvider, useForm, useFormContext } from 'react-hook-form'
import { Roles } from '@typings/roles'
import i18n from '@services/i18n/i18n'
import { EditableFile } from '@typings/files'
import { getFileByKey } from '@utils/files'
import { DefaultSnackbar } from '@shared/components/ui/DefaultSnackbar'
import { useErrorMessage } from '@hooks/useErrorMessage'
import { useFileUpload } from '@hooks/useFileUpload'
import { useUnsavedChanges } from '@hooks/useUnsavedChanges'

import { ProductsProvider, useProductsContext } from '../provider/ProductsProvider'
import { ProductCreateEditInputs } from '../components/ProductCreateEditInputs'

type ProductsInputType = Omit<UpdateIncentiveInput, 'texts'> & {
  files: Record<string, EditableFile<any> | null>
  texts: Record<string, string>
}

export const ProductEditViewContent: React.FC = () => {
  const { t } = useTranslation()
  const { id } = useParams<{ id: string }>()

  const navigate = useNavigate()

  const [loading, setLoading] = React.useState(false)
  const [showToast, setShowToast] = React.useState(false)
  const [deleted, setDeleted] = React.useState(false)

  const { setMessageByCode, errorMessage } = useErrorMessage()
  const { handleDelete, updateSelectedItem } = useProductsContext()

  const { formState: { isDirty, isValid, isSubmitted }, handleSubmit, reset } = useFormContext()

  useUnsavedChanges(isDirty && (!isSubmitted || !!errorMessage) && !deleted)

  const [updateIncentive] = useUpdateIncentiveMutation()

  const { data, loading: jobLoading } = useIncentiveQuery({
    variables: {
      id: id as string
    },
    skip: !id
  })

  const { uploadFile } = useFileUpload()

  const [deleteFile] = useDeleteFileMutation()
  const [updateFile] = useUpdateFileMutation()

  React.useEffect(() => {
    if (data?.incentive) {
      reset({
        status: data.incentive.status,
        code: data.incentive.couponCode,
        link: data.incentive.link,
        contact: data.incentive.contact,
        texts: data.incentive.texts,
        files: {
          keyvisual: getFileByKey('keyvisual', data.incentive.files)
        },
        ...(isRole(Roles.ADMIN) && {
          level: data.incentive.level,
          companyId: data.incentive.company?.id || ''
        })
      })
    }
  }, [data])

  const { data: companyData, loading: companyLoading } = useCompaniesNameOnlyQuery({
    skip: !isRole(Roles.ADMIN)
  })

  const onSubmit = handleSubmit(async (input) => {
    setLoading(true)

    try {
      await updateIncentive({
        variables: {
          id: id as string,
          data: {
            ...(isRole(Roles.ADMIN) && { level: input.level }),
            ...(isRole(Roles.ADMIN) && { companyId: input.companyId }),
            status: input.status,
            couponCode: input.code,
            contact: input.contact?.toLowerCase(),
            link: input.link,
            texts: Object.keys(input.texts).filter((key) => !!input.texts[key]).map((filtered) => ({
              key: filtered,
              lang: i18n.resolvedLanguage,
              value: input.texts[filtered]
            }))
          }
        }
      })

      Object.keys(input.files)?.forEach(async (key) => {
        const file = input.files[key]

        const fileId = getFileByKey(key, data?.incentive?.files)?.id

        if (file?.upload) {
          await uploadFile(
            file.upload.file,
            file.upload.data.key,
            'Incentive',
            id as string,
            file.config
          )
        } else if (!file && fileId) {
          await deleteFile({
            variables: { id: fileId }
          })
        } else if (file && fileId) {
          await updateFile({
            variables: {
              id: fileId as string,
              data: {
                config: file.config
              }
            }
          })
        }
      })

      setShowToast(true)
      setLoading(false)
    } catch (e) {
      setLoading(false)
      setShowToast(true)
      setMessageByCode(e)
    }
  })

  const deleteCallback = () => {
    setDeleted(true)
    navigate('/products')
  }

  const onDelete = async () => {
    if (!data?.incentive) {
      return
    }

    updateSelectedItem(data.incentive)
    handleDelete(false, deleteCallback)
  }

  return (
    <CreateEditLayout
      title={t('products.editTitle')}
      description={
        <Trans
          i18nKey={'products.editDescription'}
          values={{ name: data?.incentive?.texts.name }}
          components={{ b: <strong /> }}
        />
      }
      loading={companyLoading || jobLoading}
      headerExtension={(
        <Box sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center'
        }}
        >
          <Box mr={2}>
            <StatusSelectInput formKey="status" options={{ required: true }} />
          </Box>
          <DeleteIconButton onClick={onDelete} />
        </Box>
      )}
      buttonComp={(
        <LoadingButton
          variant="contained"
          type='submit'
          loading={loading}
          onClick={onSubmit}
          disabled={!isDirty || !isValid}
        >
          {t('common.save')}
        </LoadingButton>
      )}
    >
      <ProductCreateEditInputs companies={companyData?.companies} loading={loading}/>

      <DefaultSnackbar
        open={showToast}
        severity={errorMessage ? 'error' : 'success'}
        onClose={() => setShowToast(false)}
        message={errorMessage || t('products.entryEdited', { name: data?.incentive?.texts.name })}
      />
    </CreateEditLayout>
  )
}

export const ProductEditView:React.FC = () => {
  const formData = useForm<ProductsInputType>({
    defaultValues: {
      status: Status.Draft
    },
    mode: 'onChange'
  })

  return (
    <ProductsProvider disableFetching>
      <FormProvider {...formData}>
        <ProductEditViewContent />
      </FormProvider>
    </ProductsProvider>
  )
}
